import type { ButtonHTMLAttributes, InputHTMLAttributes, PropsWithChildren, ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { clsx } from "clsx";

export function Button({ className, variant = "primary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger" }) {
  return <button className={clsx("button", `button-${variant}`, className)} {...props} />;
}

export function IconButton({ label, children, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return <button className={clsx("icon-button", className)} aria-label={label} title={label} {...props}>{children}</button>;
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx("input", className)} {...props} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input select" {...props} />;
}

export function Badge({ tone = "neutral", children }: PropsWithChildren<{ tone?: "neutral" | "success" | "warning" | "danger" | "info" }>) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Field({ label, hint, children }: PropsWithChildren<{ label: string; hint?: string }>) {
  return <label className="field"><span className="field-label">{label}</span>{children}{hint && <span className="field-hint">{hint}</span>}</label>;
}

export function Dialog({ open, onOpenChange, title, description, children, wide = false, stacked = false }: PropsWithChildren<{ open: boolean; onOpenChange: (open: boolean) => void; title: string; description?: string; wide?: boolean; stacked?: boolean }>) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={clsx("dialog-overlay", stacked && "dialog-overlay-stacked")} />
        <DialogPrimitive.Content className={clsx("dialog-content", wide && "dialog-wide", stacked && "dialog-content-stacked")}>
          <div className="dialog-header">
            <div><DialogPrimitive.Title className="dialog-title">{title}</DialogPrimitive.Title>{description && <DialogPrimitive.Description className="dialog-description">{description}</DialogPrimitive.Description>}</div>
            <DialogPrimitive.Close asChild><IconButton label="关闭"><X size={18} /></IconButton></DialogPrimitive.Close>
          </div>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Drawer({ open, onOpenChange, title, children }: PropsWithChildren<{ open: boolean; onOpenChange: (open: boolean) => void; title: string }>) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content className="drawer-content">
          <div className="dialog-header"><DialogPrimitive.Title className="dialog-title">{title}</DialogPrimitive.Title><DialogPrimitive.Close asChild><IconButton label="关闭"><X size={18} /></IconButton></DialogPrimitive.Close></div>
          <div className="drawer-body">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><strong>{title}</strong><span>{description}</span>{action}</div>;
}

export function LoadingState({ label = "正在加载数据" }: { label?: string }) {
  return <div className="loading-state"><Loader2 className="spin" size={20} /><span>{label}</span></div>;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  loading = false,
}: {
  page: number;
  pageSize: 50 | 100 | 200;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: 50 | 100 | 200) => void;
  loading?: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pagination-bar">
      <span>共 {total} 条</span>
      <label>
        每页
        <select
          className="input select pagination-size"
          value={pageSize}
          disabled={loading}
          onChange={(event) => onPageSizeChange(Number(event.target.value) as 50 | 100 | 200)}
        >
          <option value={50}>50 条</option>
          <option value={100}>100 条</option>
          <option value={200}>200 条</option>
        </select>
      </label>
      <IconButton
        label="上一页"
        disabled={loading || page <= 1}
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        <ChevronLeft size={17} />
      </IconButton>
      <strong>第 {Math.min(page, totalPages)} / {totalPages} 页</strong>
      <IconButton
        label="下一页"
        disabled={loading || page >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        <ChevronRight size={17} />
      </IconButton>
    </div>
  );
}
